// src/app/components/onboarding/stage2-step-detail.tsx
/**
 * One stored version of a Stage 2 step, readable: what the step produced,
 * laid out for that step (belief cards with their evidence, the anchor
 * levels side by side, persona cards, the battery by form), then why it
 * passed or failed, then the record behind it (each attempt's prompt and
 * reply, the sources, the system prompt) folded away.
 */
import React, { useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Quote, X } from "lucide-react";
import type { Framework, GenerationStepDetail, ValidationProblem } from "@/api/onboarding-api";
import { BATTERY_FORMS, USE_CASES } from "@/api/onboarding-api";
import { PromptView, ReplyView, SourcesView, SystemPromptView } from "./stage2-record-views";

const STEP_TITLES: Record<string, string> = {
  beliefs: "Beliefs",
  category_check: "Category-default check",
  anchors: "Rubric anchors",
  personas: "Personas",
  variants: "Name variants",
  questions: "Question battery",
};

const muted: React.CSSProperties = { color: "var(--muted-foreground)", fontSize: "var(--text-xs)" };
const heading: React.CSSProperties = {
  fontSize: "var(--text-xs)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
  color: "var(--muted-foreground)", margin: "0 0 8px 0",
};
const item: React.CSSProperties = {
  border: "1px solid var(--border)", borderRadius: 10, padding: 14, backgroundColor: "var(--background)",
};
const chip = (tone: "neutral" | "teal" | "coral" | "amber" = "neutral"): React.CSSProperties => {
  const color = { neutral: "var(--muted-foreground)", teal: "var(--alpha-teal)", coral: "var(--infrared-coral)",
    amber: "var(--signal-amber, #B7791F)" }[tone];
  return {
    display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 8px", borderRadius: 999,
    fontSize: 11, fontWeight: 600, color, border: `1px solid ${color}`, whiteSpace: "nowrap",
  };
};
const pre: React.CSSProperties = {
  whiteSpace: "pre-wrap", fontSize: 11, maxHeight: 360, overflow: "auto", padding: 10, margin: 0,
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 6,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

const useCaseLabel = (key?: string) => USE_CASES.find((u) => u.key === key)?.label ?? key ?? "";

function Fold({ title, children, open = false }: { title: React.ReactNode; children: React.ReactNode; open?: boolean }) {
  const [isOpen, setOpen] = useState(open);
  return (
    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1"
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "var(--text-sm)",
          fontWeight: 600, color: "var(--foreground)" }}>
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {title}
      </button>
      {isOpen && <div className="mt-2">{children}</div>}
    </div>
  );
}

function Problems({ problems }: { problems: ValidationProblem[] }) {
  if (!problems.length) return null;
  return (
    <ul style={{ margin: "6px 0 0 18px", listStyle: "disc" }}>
      {problems.map((p, i) => (
        <li key={i} style={{ fontSize: "var(--text-xs)", color: (p as any).severity === "yellow" ? "var(--foreground)" : "var(--infrared-coral)" }}>
          {p.message}
        </li>
      ))}
    </ul>
  );
}

// ── Per-step output ─────────────────────────────────────────────────────────

function BeliefsOutput({ beliefs }: { beliefs: any[] }) {
  return (
    <div className="space-y-3">
      {beliefs.map((b) => (
        <div key={b.id} style={item}>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={chip("teal")}>{b.id}</span>
            <span style={{ fontSize: "var(--text-sm)", fontWeight: 700 }}>{b.label}</span>
          </div>
          <p style={{ fontSize: "var(--text-sm)", margin: "8px 0 4px 0", color: "var(--foreground)" }}>{b.claim}</p>
          {b.why_it_matters && (
            <p style={{ ...muted, margin: "0 0 8px 0" }}><strong>Why it matters:</strong> {b.why_it_matters}</p>
          )}
          {(b.evidence || []).map((e: any, i: number) => (
            <div key={i} className="flex gap-2 mt-1" style={{ borderLeft: "3px solid var(--alpha-teal)", paddingLeft: 10 }}>
              <Quote className="w-3 h-3 flex-shrink-0" style={{ marginTop: 3, color: "var(--alpha-teal)" }} />
              <div>
                <div style={{ fontSize: "var(--text-xs)", fontStyle: "italic", color: "var(--foreground)" }}>{e.quote}</div>
                <div style={muted}>
                  {e.source}{e.source_label || e.url ? ` · ${e.source_label || e.url}` : ""} · verified word for word
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function CategoryOutput({ verdicts, names }: { verdicts: Record<string, any>; names: Map<string, string> }) {
  return (
    <div className="space-y-2">
      {Object.entries(verdicts).map(([id, v]) => (
        <div key={id} style={{ ...item, padding: 10 }} className="flex items-start gap-3">
          <span style={chip(v.category_default ? "amber" : "teal")}>
            {v.category_default ? <AlertTriangle className="w-3 h-3" /> : <Check className="w-3 h-3" />}
            {v.category_default ? "Category default" : "Specific"}
          </span>
          <div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>{names.get(id) ?? id} <span style={muted}>· {id}</span></div>
            <div style={muted}>{v.reason}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnchorsOutput({ anchors, names }: { anchors: Record<string, any>; names: Map<string, string> }) {
  const levels = [
    { key: "score_0", ex: "0", label: "0 · Absent", tone: "var(--infrared-coral)" },
    { key: "score_1", ex: "1", label: "1 · Implied", tone: "var(--signal-amber, #B7791F)" },
    { key: "score_2", ex: "2", label: "2 · Stated", tone: "var(--alpha-teal)" },
  ];
  return (
    <div className="space-y-3">
      {Object.entries(anchors).map(([id, a]) => (
        <div key={id} style={item}>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 700, marginBottom: 8 }}>
            {names.get(id) ?? id} <span style={muted}>· {id}</span>
          </div>
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
            {levels.map((l) => (
              <div key={l.key} style={{ borderTop: `3px solid ${l.tone}`, paddingTop: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: l.tone }}>{l.label}</div>
                <div style={{ fontSize: "var(--text-xs)", margin: "4px 0", color: "var(--foreground)" }}>{a[l.key]}</div>
                {a.examples?.[l.ex] && (
                  <div style={{ ...muted, fontStyle: "italic" }}>e.g. “{a.examples[l.ex]}”</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PersonasOutput({ personas }: { personas: any[] }) {
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
      {personas.map((p) => (
        <div key={p.id} style={item}>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: "var(--text-sm)", fontWeight: 700 }}>{p.label}</span>
            <span style={chip()}>{p.id}</span>
            <span style={chip("teal")}>{useCaseLabel(p.use_case)}</span>
          </div>
          <p style={{ fontSize: "var(--text-xs)", margin: "8px 0 4px 0" }}>{p.description}</p>
          <p style={{ ...muted, margin: 0 }}><strong>Goal:</strong> {p.goal}</p>
        </div>
      ))}
    </div>
  );
}

function VariantsOutput({ output }: { output: any }) {
  const gates = output.misidentification || [];
  return (
    <div className="space-y-3">
      <div>
        <p style={heading}>Names an answer may use for the brand</p>
        <div className="flex gap-2 flex-wrap">
          {(output.name_variants || []).map((v: string) => <span key={v} style={chip("teal")}>{v}</span>)}
        </div>
      </div>
      <div>
        <p style={heading}>Could be confused with</p>
        {gates.length === 0 ? <p style={muted}>None: the name is unambiguous.</p> : (
          <div className="space-y-2">
            {gates.map((g: any, i: number) => (
              <div key={i} style={{ ...item, padding: 10 }}>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>{g.name}</div>
                <div style={muted}>{[g.descriptor, g.location, g.domain].filter(Boolean).join(" · ")}</div>
                <div style={{ fontSize: "var(--text-xs)", marginTop: 4 }}><strong>How to tell:</strong> {g.how_to_tell}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionsOutput({ output, names, personaNames }: {
  output: any; names: Map<string, string>; personaNames: Map<string, string>;
}) {
  const queries: any[] = output.queries || [];
  const checks = output.context_checks || {};
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" style={muted}>
        <span>{queries.length} questions</span>
        {output.cell && <span style={chip("amber")}>Regenerated cell: {output.cell.persona_id} × {output.cell.form}</span>}
        {output.lint && (
          <span style={chip(output.lint.status === "green" ? "teal" : output.lint.status === "yellow" ? "amber" : "coral")}>
            Linter: {output.lint.status}
          </span>
        )}
        {output.lint?.battery_hash && <span>battery {String(output.lint.battery_hash).slice(0, 10)}</span>}
      </div>
      {BATTERY_FORMS.map((form) => {
        const rows = queries.filter((q) => q.category === form.key);
        if (!rows.length) return null;
        return (
          <div key={form.key}>
            <p style={heading}>{form.label} · {rows.length}{output.form_targets?.[form.key] !== undefined ? ` of ${output.form_targets[form.key]}` : ""}</p>
            <div className="space-y-2">
              {rows.map((q) => {
                const check = checks[q.id];
                return (
                  <div key={q.id} style={{ ...item, padding: 10 }}>
                    <div className="flex items-start gap-2">
                      <span style={chip()}>{q.id}</span>
                      <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>{q.text}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span style={chip("teal")}>{personaNames.get(q.persona_id) ?? q.persona ?? q.persona_id}</span>
                      <span style={chip()}>{useCaseLabel(q.use_case)}</span>
                      {(q.variable_ids || []).map((b: string) => (
                        <span key={b} style={chip()} title={names.get(b) ?? b}>{b}{names.get(b) ? ` · ${names.get(b)}` : ""}</span>
                      ))}
                      {check && (
                        <span style={chip(check.present ? "teal" : "coral")}>
                          {check.present ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {check.present ? `context: “${check.context}”` : "no category context"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepOutput({ step, output, framework }: { step: string; output: any; framework?: Framework | null }) {
  if (!output) return null;
  const names = new Map<string, string>(
    (framework?.reasoning_variables || []).map((v) => [v.id, v.label || v.claim]),
  );
  for (const b of output.beliefs || []) names.set(b.id, b.label || b.claim);
  const personaNames = new Map<string, string>(
    (framework?.target_users || []).filter((p) => p.id).map((p) => [p.id as string, p.label]),
  );
  switch (step) {
    case "beliefs": return <BeliefsOutput beliefs={output.beliefs || []} />;
    case "category_check": return <CategoryOutput verdicts={output.verdicts || {}} names={names} />;
    case "anchors": return <AnchorsOutput anchors={output.anchors || {}} names={names} />;
    case "personas": return <PersonasOutput personas={output.personas || []} />;
    case "variants": return <VariantsOutput output={output} />;
    case "questions": return <QuestionsOutput output={output} names={names} personaNames={personaNames} />;
    default: return <pre style={pre}>{JSON.stringify(output, null, 2)}</pre>;
  }
}

// ── The panel ───────────────────────────────────────────────────────────────

export function StepDetail({
  detail, framework, onClose,
}: { detail: GenerationStepDetail; framework?: Framework | null; onClose: () => void }) {
  const passed = detail.status === "passed";
  const problems = detail.validation?.problems ?? [];
  const rejected = (detail.validation as any)?.rejected_output;
  const included = detail.sources.filter((s) => s.included).length;
  return (
    <div style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 20 }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 style={{ fontSize: "var(--text-base)", fontWeight: 700, margin: 0 }}>
              {STEP_TITLES[detail.step] ?? detail.step} <span style={{ ...muted, fontWeight: 400 }}>v{detail.version}</span>
            </h3>
            <span style={chip(passed ? "teal" : "coral")}>
              {passed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}{passed ? "Passed" : "Failed"}
            </span>
          </div>
          <div className="flex gap-3 flex-wrap mt-1" style={muted}>
            <span>{detail.model}</span>
            <span>prompt {detail.prompt_version}</span>
            <span>${Number(detail.cost_usd || 0).toFixed(3)}</span>
            <span>{detail.attempts.length} attempt{detail.attempts.length === 1 ? "" : "s"}</span>
            <span>{included} source{included === 1 ? "" : "s"}</span>
            {detail.created_at && <span>{new Date(detail.created_at).toLocaleString()}</span>}
          </div>
        </div>
        <button onClick={onClose} title="Close"
          style={{ background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: 4, cursor: "pointer", color: "var(--foreground)" }}>
          <X className="w-4 h-4" />
        </button>
      </div>

      {!passed && (
        <div className="mt-4 p-3 rounded-lg" style={{ border: "1px solid var(--infrared-coral)" }}>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--infrared-coral)" }}>
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Not saved to the framework: it did not pass validation after {detail.attempts.length} attempt{detail.attempts.length === 1 ? "" : "s"}.
          </div>
          <Problems problems={problems} />
        </div>
      )}
      {passed && problems.length > 0 && (
        <div className="mt-4 p-3 rounded-lg" style={{ border: "1px solid var(--signal-amber, var(--border))" }}>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Passed, with findings for the reviewer</div>
          <Problems problems={problems} />
        </div>
      )}

      <div className="mt-4">
        <p style={heading}>{passed ? "What this step produced" : "What the model returned (rejected)"}</p>
        {passed
          ? <StepOutput step={detail.step} output={detail.output} framework={framework} />
          : rejected ? <StepOutput step={detail.step} output={rejected} framework={framework} />
            : <p style={muted}>No usable reply.</p>}
      </div>

      {detail.attempts.map((attempt) => (
        <Fold key={attempt.attempt} title={
          <span className="flex items-center gap-2">
            Attempt {attempt.attempt}
            {attempt.error
              ? <span style={chip("coral")}>call failed</span>
              : (attempt.problems?.length ?? 0) > 0
                ? <span style={chip("coral")}>{attempt.problems!.length} problem{attempt.problems!.length === 1 ? "" : "s"}</span>
                : <span style={chip("teal")}>clean</span>}
          </span>
        }>
          {(attempt.problems?.length ?? 0) > 0 && <Problems problems={attempt.problems!} />}
          <p style={{ ...heading, marginTop: 10 }}>Prompt</p>
          <PromptView text={attempt.prompt} />
          <p style={{ ...heading, marginTop: 10 }}>Reply</p>
          <ReplyView text={attempt.reply} error={attempt.error} />
        </Fold>
      ))}

      <Fold title={`Sources given (${included} of ${detail.sources.length})`}>
        <SourcesView sources={detail.sources} systemPrompt={detail.system_prompt} />
      </Fold>
      <Fold title="System prompt (brand and sources)">
        <SystemPromptView text={detail.system_prompt} />
      </Fold>
    </div>
  );
}
